import {
    find,
    get,
    map,
    parseInt,
    toString,
    forEach,
    size,
    concat,
    uniq,
    isArray,
    isString,
    isBoolean,
    cloneDeep,
} from 'lodash';
import apiClient, { type IApiErrorResponse } from '../../../shared/services/api-client.service';
import useMachinesStore from '../store/machine.store';
import type {
    IMachineFilters,
    IMachineGridDataItem,
    IMachineGridDataMeta,
    ISearchMachineCommand,
} from '../../../shared/interfaces/machine-store.interface';
import useCompatibilityStore from '../../compatibility/store/compatibility.store';
import type {
    ICompatibilityItem,
    ICompatibilityModalMachineApi,
    ICompatibilityTableItem,
} from '../../../shared/interfaces/compatibility-store.interface';
import type { AxiosError, AxiosResponse } from 'axios';
import type { IMachineProperty } from '../../../shared/interfaces/machine-property.interface';
import { UIService } from '../../../shared/services/ui.service';
import { coerceSpecValue } from '../../../shared/forms/coerce-spec-value';

export class MachineService {
    private readonly pendingAddNewItemStorageKey = 'pending_add_new_item';

    constructor(
        private machinesStore = useMachinesStore(),
        private compatibilityStore = useCompatibilityStore(),
        private apiClients = apiClient,
        private uiService: UIService = new UIService()
    ) {
        this.machinesStore = machinesStore;
        this.compatibilityStore = compatibilityStore;
        this.apiClients = apiClient;
        this.uiService = new UIService();
    }

    private resolveMachineModelFieldName(): string {
        const currentValues = this.machinesStore.current.values ?? {};

        if ('core-model' in currentValues) {
            return 'core-model';
        }

        if ('core-models' in currentValues) {
            return 'core-models';
        }

        if ('model' in currentValues) {
            return 'model';
        }

        return 'core-model';
    }

    private savePendingAddNewItem(fieldName: string, value: string, type: 'model' | 'category'): void {
        if (typeof window === 'undefined') {
            return;
        }

        const currentFormValues = cloneDeep(this.machinesStore.current.values ?? {});
        const nextFormValues = {
            ...currentFormValues,
            [fieldName]: value,
        };

        localStorage.setItem(
            this.pendingAddNewItemStorageKey,
            JSON.stringify({
                fieldName,
                value,
                type,
                formValues: nextFormValues,
            })
        );
    }

    async loadMachineFilters(): Promise<void> {
        this.machinesStore.setMachineLoading(true);

        try {
            const machineFilters: IMachineFilters = await this.apiClients.get('/machines/models');
            const modelFilters: string[] = get(machineFilters, 'data') ?? [];

            this.machinesStore.setMachineFilters({
                model: concat('All', modelFilters),
                min_base_weight: 0,
                tractor_loader_only: false,
            });

            this.machinesStore.setMachineLoading(false);
        } catch (error) {
            this.machinesStore.setMachineLoading(false);
            this.uiService.notifyApiErrorAction(error);
        }
    }

    async deleteMachineModel(modelId: string): Promise<void> {
        this.machinesStore.setMachineLoading(true);

        try {
            await this.apiClients.delete(`/machines/models/${modelId}`);
            this.machinesStore.setMachineLoading(false);

            await this.loadMachineFilters();
            await this.loadMachineModels();
            await this.loadAllMachineProperties();
        } catch (error) {
            this.machinesStore.setMachineLoading(false);
            this.uiService.notifyApiErrorAction(error);
        }
    }

    async updateMachineModel(
        modelId: string,
        payload: {
            name: string;
            description: string;
            is_active: boolean;
        }
    ): Promise<void> {
        this.machinesStore.setMachineLoading(true);

        try {
            await this.apiClients.patch(`/machines/models/${modelId}`, payload);
            await this.loadMachineFilters();
            await this.loadMachineModels();
            await this.loadAllMachineProperties();
        } catch (error) {
            this.uiService.notifyApiErrorAction(error);
        } finally {
            this.machinesStore.setMachineLoading(false);
        }
    }

    async addMachineModel(payload: { name: string; description: string }): Promise<void> {
        this.machinesStore.setMachineLoading(true);

        try {
            await this.apiClients.post('/machines/models', payload);

            const modelFieldName = this.resolveMachineModelFieldName();
            this.savePendingAddNewItem(modelFieldName, payload.name, 'model');
            await this.loadMachineFilters();
            await this.loadMachineModels();
            await this.loadAllMachineProperties();
        } catch (error) {
            this.uiService.notifyApiErrorAction(error);
        } finally {
            this.machinesStore.setMachineLoading(false);
        }
    }

    async getMachineAssociationCount(modelName: string): Promise<number> {
        try {
            const machineData = await this.apiClients.get('/machines', {
                params: {
                    model: modelName,
                    exact_match: true,
                    page_num: 1,
                    page_size: 10,
                },
            });

            return Number(get(machineData.data, 'total_items', 0));
        } catch (error: any) {
            const status = error?.response?.status;

            if (status === 404) {
                return 0;
            }

            this.uiService.notifyApiErrorAction(error);
            throw error;
        }
    }

    async loadMachineGridData(command: ISearchMachineCommand): Promise<void> {
        const serachParams = {
            model: toString(command.model),
            name: toString(command.name),
            exact_match: command.exact_match ?? true,
            page_num: command.page_num ?? 1,
            page_size: command.page_size ?? 15,
        };

        const modelValue = toString(command.model).trim();

        if (!command.model || !size(command.model) || modelValue.match(/^all$/i)) {
            // eslint-disable-next-line @typescript-eslint/ban-ts-comment
            // @ts-ignore
            delete serachParams.model;
        }

        if (!command.name) {
            // eslint-disable-next-line @typescript-eslint/ban-ts-comment
            // @ts-ignore
            delete serachParams.name;
        }

        try {
            const machineData = await this.apiClients.get('/machines', {
                params: serachParams,
            });

            const meta: IMachineGridDataMeta = {
                current_page: get(machineData.data, 'current_page'),
                total_items: get(machineData.data, 'total_items'),
                items_per_page: get(machineData.data, 'items_per_page'),
                total_pages: get(machineData.data, 'total_pages'),
            };

            const getMachineMinMaxValue = (mmin: string, mmax: string): string => {
                if (mmin !== '' && mmax !== '') {
                    return `${mmin} - ${mmax}`;
                }
                if (mmin === '' && mmax !== '') {
                    return mmax;
                }
                if (mmin !== '' && mmax === '') {
                    return mmin;
                }
                return '';
            };

            const getMachineSpecValue = (index: number, key: string): string => {
                return toString(
                    get(
                        find(get(get(machineData.data.items, index), 'specs'), (x) => x.property_name === key),
                        'value'
                    )
                );
            };

            const items: IMachineGridDataItem[] = map(machineData.data.items, (x, i) => {
                return {
                    id: get(x, 'id'),
                    name: get(x, 'name'),
                    description: get(x, 'description'),
                    model: getMachineSpecValue(i, 'core.model'),
                    engineHp: getMachineSpecValue(i, 'core.engine.hp'),
                    ptoHp: getMachineSpecValue(i, 'core.pto.hp'),
                    base_weight: parseInt(
                        toString(
                            getMachineSpecValue(i, 'core.base_weight') === ''
                                ? '0'
                                : getMachineSpecValue(i, 'core.base_weight')
                        ),
                        10
                    ),
                    loader: getMachineSpecValue(i, 'core.has_loader'),
                    loader_style: getMachineSpecValue(i, 'mount.loader.style'),
                    mount_3pt_type: getMachineSpecValue(i, 'mount.3pt.type'),
                    drawbar_max_load: getMachineSpecValue(i, 'mount.drawbar.max_load'),
                    max_lift_capacity: getMachineSpecValue(i, 'lowerlinkend.max_lift_capacity'),
                    front_pressure: getMachineSpecValue(i, 'hydraulics.front.max_pressure'),
                    front_flow_rate: getMachineMinMaxValue(
                        getMachineSpecValue(i, 'hydraulics.front.min_flow_rate'),
                        getMachineSpecValue(i, 'hydraulics.front.max_flow_rate')
                    )
                        ? `${getMachineMinMaxValue(
                              getMachineSpecValue(i, 'hydraulics.front.min_flow_rate'),
                              getMachineSpecValue(i, 'hydraulics.front.max_flow_rate')
                          )}`
                        : '',
                    actions: get(x, 'id'),
                };
            });

            this.machinesStore.setMachineGridDataMeta(meta);
            this.machinesStore.setMachineGridDataItems(items);
            this.machinesStore.setMachineLoading(false);
        } catch (error: any) {
            this.machinesStore.setMachineGridDataMeta({
                current_page: 0,
                items_per_page: 0,
                total_items: 0,
                total_pages: 0,
            });
            this.machinesStore.setMachineGridDataItems([]);
            this.machinesStore.setMachineLoading(false);

            const status = error?.response?.status;
            if (status === 404) {
                return;
            }

            if (status === 422) {
                const validationMessage = error?.response?.data?.detail?.[0]?.msg ?? 'Validation error';

                this.uiService.notifyApiErrorAction(validationMessage);

                throw error;
            }

            if (serachParams.name === '') {
                this.uiService.notifyApiErrorAction(error);
            }

            throw error;
        }
    }

    async loadMachineCompatibleImplements(name: string): Promise<void> {
        this.compatibilityStore.setCompatibleModalLoading(true);

        try {
            const machineData: ICompatibilityModalMachineApi = await this.apiClients.get(
                `/compatibility/machines/${name}`
            );

            const approved = get(machineData, 'data.compatible_implements.approved', []) as ICompatibilityItem[];
            const maybeApproved = get(
                machineData,
                'data.compatible_implements.maybe_approved',
                []
            ) as ICompatibilityItem[];

            const tableCompatibleItems: ICompatibilityTableItem[] = [
                ...approved.map((item) => ({
                    id: item.id,
                    name: item.name,
                    compatibility: 'APPROVED',
                })),
                ...maybeApproved.map((item) => ({
                    id: item.id,
                    name: item.name,
                    compatibility: 'MAYBE_APPROVED',
                })),
            ];

            this.compatibilityStore.setCompatibleModalData({
                id: get(machineData, 'data.machine_id', ''),
                name: get(machineData, 'data.machine_name', ''),
                type: 'dialog_compatibility_implement_title',
                compatible_items: approved,
                maybe_compatible_items: maybeApproved,
                not_compatible_items: [],
                tableCompatibleItems,
            });

            this.compatibilityStore.setCompatibleModalLoading(false);
        } catch (error) {
            this.compatibilityStore.setCompatibleModalLoading(false);
            this.uiService.notifyApiErrorAction(error);
        }
    }

    async deleteMachine(id: string): Promise<void> {
        try {
            await this.apiClients.delete(`/machines/${id}`);
            this.machinesStore.deleteMachine(id);
            this.machinesStore.setMachineLoading(false);
        } catch (error) {
            this.machinesStore.setMachineLoading(false);
            this.uiService.notifyApiErrorAction(error);
        }
    }

    async loadMachineData(id: string): Promise<void> {
        this.machinesStore.setMachineLoading(true);

        try {
            const machine: AxiosResponse = await this.apiClients.get(`/machines/${id}`);
            this.machinesStore.setMachineData(machine.data);
            this.machinesStore.setMachineLoading(false);
        } catch (error) {
            this.machinesStore.resetMachineData();
            this.machinesStore.setMachineLoading(false);
            this.uiService.notifyApiErrorAction(error);
        }
    }

    async loadMachineModels(): Promise<void> {
        this.machinesStore.setCurrentMachineLoading(true);

        try {
            const machineFilters: IMachineFilters = await this.apiClients.get('/machines/models');
            const modelFilters: string[] = get(machineFilters, 'data') ?? [];
            this.machinesStore.setCurrentMachineModels(modelFilters);
            this.machinesStore.setCurrentMachineLoading(false);
        } catch (error) {
            this.machinesStore.setCurrentMachineLoading(false);
            this.uiService.notifyApiErrorAction(error);
        }
    }

    async loadAllMachineProperties(): Promise<void> {
        this.machinesStore.setCurrentMachineLoading(true);

        try {
            const allMachineProperty: IMachineProperty[] = await this.apiClients.get('/machines/properties');
            const machineProperty: IMachineProperty[] = get(allMachineProperty, 'data') ?? [];
            this.machinesStore.setcurrentMachinePropertyItmes(machineProperty);

            const allMachinePropertyCategories: string[] = uniq(map(machineProperty, (x) => x.label.group));
            this.machinesStore.setcurrentMachinePropertyCategories(allMachinePropertyCategories);

            this.machinesStore.setCurrentMachineLoading(false);
        } catch (error) {
            this.machinesStore.setCurrentMachineLoading(false);
            this.machinesStore.resetcurrentMachinePropertyItmes();
            this.machinesStore.resetcurrentMachinePropertyCategories();
            this.uiService.notifyApiErrorAction(error);
        }
    }

    async saveMachine(): Promise<any> {
        this.machinesStore.setCurrentMachineErrors(null);
        this.machinesStore.setCurrentMachineLoading(true);

        const data = this.reworkMachineData(cloneDeep(this.machinesStore.current.confirmValues));

        return this.apiClients
            .post('/machines', {
                ...data,
            })
            .then((saveMachineResponse) => {
                this.uiService.notifyAction({
                    title: 'Success!',
                    action: null,
                    message: 'The creation of the new machine is completed',
                    type: 'success',
                });
                return Promise.resolve(saveMachineResponse.data);
            })
            .catch((error: AxiosError<IApiErrorResponse>) => {
                this.uiService.notifyApiErrorAction(error);
                this.machinesStore.setCurrentMachineErrors(error.response?.data ?? null);
                return Promise.reject(error);
            });
    }

    async updateMachine(id: string): Promise<any> {
        this.machinesStore.setCurrentMachineErrors(null);
        this.machinesStore.setCurrentMachineLoading(true);

        const data = this.reworkMachineData(cloneDeep(this.machinesStore.current.confirmValues));

        return this.apiClients
            .patch(`/machines/${id}`, {
                ...data,
            })
            .then((updateMachineResponse) => {
                this.uiService.notifyAction({
                    message: 'The update of machine is completed',
                    title: 'Success!',
                    action: null,
                    type: 'success',
                });
                return Promise.resolve(updateMachineResponse.data);
            })
            .catch((error: AxiosError<IApiErrorResponse>) => {
                this.uiService.notifyApiErrorAction(error);
                this.machinesStore.setCurrentMachineErrors(error.response?.data ?? null);
                return Promise.reject(error);
            });
    }

    reworkMachineData(data: Record<string, any>): Record<string, any> {
        const name: string = get(data, 'name');
        const description: string = get(data, 'description');
        const comment: string = get(data, 'comment');

        const specs: { property_name: string; value: any }[] = [];

        delete data.name;
        delete data.icon;
        delete data.__dummy__;
        delete data.description;
        delete data.comment;
        const dataTypeByName: Record<string, string> = Object.fromEntries(
            map(this.machinesStore.current.properties, (property) => [property.name, property.data_type])
        );

        forEach(data, (value, property_name) => {
            if (property_name === 'icon') {
                return;
            }

            if (isBoolean(value) && value === false) {
                return;
            }

            if (isString(value) && value === '') {
                return;
            }

            if (isArray(value) && !size(value)) {
                return;
            }

            const specName = property_name.replace(/-/g, '.');
            specs.push({
                property_name: specName,
                value: coerceSpecValue(value, dataTypeByName[specName]),
            });
        });

        return {
            name,
            description,
            comment,
            specs,
        };
    }
}
